import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useEffect } from "react";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { toast } from "sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import { CommunityProvider } from "./contexts/CommunityContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AudioProvider } from "./contexts/AudioContext";
import PersistentPlayer from "./components/PersistentPlayer";
import HBCUMedia from "./pages/HBCUMedia";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import RoleSignup from "./pages/RoleSignup";
// AuthCallback no longer needed — Firebase handles OAuth internally
import Dashboard from "./pages/Dashboard";
import Feed from "./pages/Feed";
import Clips from "./pages/Clips";
import Jobs from "./pages/Jobs";
import Events from "./pages/Events";
import PropertyRentals from "./pages/PropertyRentals";
import RealEstate from "./pages/RealEstate";
import Marketplace from "./pages/Marketplace";
import Dating from "./pages/Dating";
import Chat from "./pages/Chat";
import Notifications from "./pages/Notifications";
import Mentorship from "./pages/Mentorship";
import Profile from "./pages/Profile";
import ServiceDirectory from "./pages/ServiceDirectory";
import AlumniBusinessServices from "./pages/AlumniBusinessServices";
import CategoryPage from "./pages/CategoryPage";
import Blog from "./pages/Blog";
import PaymentSuccess from "./pages/PaymentSuccess";
import VideoCall from "./pages/VideoCall";
import AdminPanel from "./pages/AdminPanel";
import AlumniAssociationPanel from "./pages/AlumniAssociationPanel";
import RecruiterPanel from "./pages/RecruiterPanel";
import SponsorPanel from "./pages/SponsorPanel";
import Bookings from "./pages/Bookings";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import InstallPrompt from "./components/InstallPrompt";
import FoundingProviderApplication from "./pages/FoundingProviderApplication";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import DeleteAccount from "./pages/DeleteAccount";
import ChildSafety from "./pages/ChildSafety";

// Register service worker for PWA
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    toast.info("New version available! Refresh to update.", {
      action: {
        label: "Refresh",
        onClick: () => {
          if (registration.waiting) {
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
          }
          window.location.reload();
        }
      },
      duration: 10000
    });
  },
  onSuccess: () => {
    console.log("App is ready for offline use");
  },
  onOffline: () => {
    toast.warning("You are offline. Some features may be limited.");
  },
  onOnline: () => {
    toast.success("Back online!");
  }
});

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-frost-white flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-slate"></div>
          <p className="text-slate-gray text-sm font-medium">Loading GradLink...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/role-signup" element={<RoleSignup />} />
        <Route path="/founding-provider" element={<FoundingProviderApplication />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/child-safety" element={<ChildSafety />} />

        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
        <Route path="/clips" element={<ProtectedRoute><Clips /></ProtectedRoute>} />
        <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
        <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
        <Route path="/rentals" element={<ProtectedRoute><PropertyRentals /></ProtectedRoute>} />
        <Route path="/real-estate" element={<ProtectedRoute><RealEstate /></ProtectedRoute>} />
        <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
        <Route path="/dating" element={<ProtectedRoute><Dating /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/chat/:userId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        <Route path="/mentorship" element={<ProtectedRoute><Mentorship /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/services" element={<ProtectedRoute><AlumniBusinessServices /></ProtectedRoute>} />
        <Route path="/business-services" element={<ProtectedRoute><AlumniBusinessServices /></ProtectedRoute>} />
        <Route path="/category/:categoryId" element={<ProtectedRoute><CategoryPage /></ProtectedRoute>} />
        <Route path="/blog" element={<ProtectedRoute><Blog /></ProtectedRoute>} />
        <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/video/:roomId" element={<ProtectedRoute><VideoCall /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
        <Route path="/alumni-association" element={<ProtectedRoute><AlumniAssociationPanel /></ProtectedRoute>} />
        <Route path="/recruiter" element={<ProtectedRoute><RecruiterPanel /></ProtectedRoute>} />
        <Route path="/sponsor" element={<ProtectedRoute><SponsorPanel /></ProtectedRoute>} />
        <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/hbcu-media" element={<ProtectedRoute><HBCUMedia /></ProtectedRoute>} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AudioProvider>
            <AuthProvider>
              <CommunityProvider>
                <AppRouter />
                <PersistentPlayer />
                <Toaster position="top-right" />
                <InstallPrompt />
              </CommunityProvider>
            </AuthProvider>
          </AudioProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
